import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ClipboardList, Plus, X, CheckCircle, Circle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ActionItems = ({ actions, functionType, periodType, year, onActionUpdated }) => {
  const { isELT } = useAuth();
  const [isAdding, setIsAdding] = useState(false);
  const [newAction, setNewAction] = useState({
    description: '',
    owner: '',
    due_date: '',
    status: 'open'
  });

  const handleAddAction = async () => {
    if (!newAction.description.trim() || !newAction.owner.trim() || !newAction.due_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await axios.post(`${API}/action-items`, {
        function: functionType,
        period_type: periodType,
        year: year,
        ...newAction
      });
      
      toast.success('Action item added');
      setNewAction({ description: '', owner: '', due_date: '', status: 'open' });
      setIsAdding(false);
      onActionUpdated?.();
    } catch (error) {
      toast.error('Failed to add action item');
    }
  };

  const handleStatusChange = async (actionId, newStatus) => {
    try {
      await axios.put(`${API}/action-items/${actionId}`, { status: newStatus });
      toast.success('Status updated');
      onActionUpdated?.();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'complete':
        return <CheckCircle className="w-4 h-4 text-emerald-600" />;
      case 'in_progress':
        return <Clock className="w-4 h-4 text-amber-600" />;
      default:
        return <Circle className="w-4 h-4 text-slate-400" />;
    }
  };

  const filteredActions = actions?.filter(a => a.function === functionType) || [];

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-6" data-testid="action-items-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-slate-400" />
          <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">
            Action Items
          </h3>
          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
            {filteredActions.filter(a => a.status !== 'complete').length} open
          </span>
        </div>
        {isELT() && !isAdding && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsAdding(true)}
            data-testid="add-action-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Action
          </Button>
        )}
      </div>

      {/* Add Action Form */}
      {isAdding && (
        <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
          <Input
            value={newAction.description}
            onChange={(e) => setNewAction({ ...newAction, description: e.target.value })}
            placeholder="Action description"
            data-testid="action-description-input"
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              value={newAction.owner}
              onChange={(e) => setNewAction({ ...newAction, owner: e.target.value })}
              placeholder="Owner (e.g., VP Sales)"
              data-testid="action-owner-input"
            />
            <Input
              type="date"
              value={newAction.due_date}
              onChange={(e) => setNewAction({ ...newAction, due_date: e.target.value })}
              data-testid="action-due-date-input"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsAdding(false);
                setNewAction({ description: '', owner: '', due_date: '', status: 'open' });
              }}
            >
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleAddAction}
              data-testid="save-action-btn"
            >
              Add Action
            </Button>
          </div>
        </div>
      )}

      {/* Actions List */}
      <div className="space-y-3">
        {filteredActions.length === 0 && !isAdding ? (
          <p className="text-sm text-slate-400 text-center py-4">
            No action items
          </p>
        ) : (
          filteredActions.map((action) => (
            <div 
              key={action.id} 
              className={cn(
                "flex items-start gap-3 p-3 rounded-lg border",
                action.status === 'complete' 
                  ? 'bg-slate-50 border-slate-100' 
                  : 'bg-white border-slate-200'
              )}
            >
              <button
                onClick={() => {
                  if (!isELT()) return;
                  const nextStatus = action.status === 'open' 
                    ? 'in_progress' 
                    : action.status === 'in_progress' 
                      ? 'complete' 
                      : 'open';
                  handleStatusChange(action.id, nextStatus);
                }}
                className="mt-0.5"
              >
                {getStatusIcon(action.status)}
              </button>
              <div className="flex-1 min-w-0">
                <p className={cn(
                  "text-sm",
                  action.status === 'complete' 
                    ? 'text-slate-400 line-through' 
                    : 'text-slate-700'
                )}>
                  {action.description}
                </p>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-slate-500">{action.owner}</span>
                  <span className="text-xs text-slate-400">
                    Due: {new Date(action.due_date).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
              </div>
              {isELT() && (
                <Select
                  value={action.status}
                  onValueChange={(val) => handleStatusChange(action.id, val)}
                >
                  <SelectTrigger className="w-[120px] h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="complete">Complete</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ActionItems;
